/**
 * Structured data for a shop landing page: the physical store behind it, plus
 * its FAQ block.
 *
 * Why this exists: Google classifies the Munich shop as a generic "Geschäft"
 * rather than a Spirituosengeschäft. The Business Profile category is set
 * correctly, so the missing half is on our side — the page carried no
 * structured data at all, and address and opening hours existed only as prose
 * inside the Strapi copy, which Google does not read as store facts.
 */

import { buildStoreMapUrl, getRetailStore } from '@/constants/retail-stores.constants';

type FaqEntry = { question?: string; answer?: string };

function stripHtml(value: string): string {
  return value
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Strapi returns the FAQ block as an array; older entries used a keyed object. */
function toFaqList(faqs: unknown): FaqEntry[] {
  if (Array.isArray(faqs)) return faqs as FaqEntry[];
  if (faqs && typeof faqs === 'object') return Object.values(faqs as Record<string, FaqEntry>);
  return [];
}

type Props = {
  sellerId?: string | null;
  faqs?: unknown;
  /** Absolute URL of the page this markup describes. */
  pageUrl?: string | null;
};

export default function ShopStructuredData({ sellerId, faqs, pageUrl }: Props): JSX.Element | null {
  const store = getRetailStore(sellerId);

  const faqItems = toFaqList(faqs)
    .filter(({ question, answer }) => question && answer)
    .map(({ question, answer }) => ({
      '@type': 'Question',
      name: stripHtml(question as string),
      acceptedAnswer: { '@type': 'Answer', text: stripHtml(answer as string) },
    }));

  const graph: Record<string, unknown>[] = [];

  if (store) {
    graph.push({
      '@context': 'https://schema.org',
      // LiquorStore is the schema.org counterpart of the Business Profile
      // category; plain LocalBusiness would repeat the "generic shop" reading.
      '@type': 'LiquorStore',
      name: store.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: store.streetAddress,
        postalCode: store.postalCode,
        addressLocality: store.addressLocality,
        addressCountry: store.addressCountry,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: store.openingDays,
          opens: store.opens,
          closes: store.closes,
        },
      ],
      hasMap: buildStoreMapUrl(store),
      ...(pageUrl ? { url: pageUrl } : {}),
    });
  }

  if (faqItems.length > 0) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems,
    });
  }

  if (graph.length === 0) return null;

  return (
    <>
      {graph.map((schema, index) => (
        <script
          key={`schema-shop-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}
